import React, { FC } from 'react';
import { ArrowRight } from 'lucide-react';
import { FormProvider } from 'react-hook-form';

import {
  ModalHeader,
  ModalDescription,
  ModalFooter,
  ModalTitle
} from '@epoch-ui/components/Modal/modal';
import { Button } from '@epoch-ui/components/Button';
import { StepBar } from '@epoch-components/step-bar/StepBar';
import { QuestionnaireFields } from '@epoch-components/QuestionnaireFields';
import { useQuestionResponseForm } from '@epoch-components/QuestionnaireFields/schema';

import { usePlusOneRsvpControlModalContext } from '../plusOneRsvpControlContext';

interface SubmitQuestionnaireContentProps {
  isOwnerQuestionnaire: boolean;
}

export const SubmitQuestionnaireContent: FC<SubmitQuestionnaireContentProps> = ({
  isOwnerQuestionnaire
}) => {
  const {
    navigation,
    ownerGuest,
    plusOneGuest,
    event: { preEventQuestions: questions },
    updatedResponses,
    setUpdatedResponses,
    updatedOwnerResponses,
    setUpdatedOwnerResponses
  } = usePlusOneRsvpControlModalContext();

  const firstName = isOwnerQuestionnaire ? ownerGuest.firstName : plusOneGuest.firstName;
  const setSurveyResponses = isOwnerQuestionnaire ? setUpdatedOwnerResponses : setUpdatedResponses;
  const initialResponses = isOwnerQuestionnaire ? updatedOwnerResponses : updatedResponses;
  const fieldName = 'surveyResponses';
  const { form, parseResponses } = useQuestionResponseForm({
    fieldName,
    questions,
    responses: initialResponses
  });

  const handleSubmission = async (): Promise<void> => {
    const isValid = await form.trigger('surveyResponses');
    if (!isValid) return;

    const newResponses = parseResponses(form.getValues());
    setSurveyResponses(newResponses);

    navigation.onClickSubmit();
  };

  return (
    <FormProvider {...form}>
      <ModalHeader className="tw-m-0 tw-flex tw-flex-col tw-gap-1">
        <ModalTitle className="tw-m-0 tw-text-2xl tw-font-medium">
          Complete {firstName}&apos;s registration
        </ModalTitle>
        <ModalDescription className="tw-m-0">{`Fill out this questionnaire to complete ${firstName}'s registration for this event.`}</ModalDescription>
      </ModalHeader>
      <div className="tw-flex tw-flex-col tw-gap-6">
        <QuestionnaireFields questions={questions} fieldNamePrefix={fieldName} />
      </div>
      <ModalFooter className="tw-mt-0 tw-flex tw-flex-row tw-justify-end">
        <div className="tw-flex tw-flex-1 tw-items-center">
          <StepBar
            totalSteps={navigation.totalStepNumber}
            currentStep={navigation.currentStepNumber}
          />
        </div>
        <div className="tw-flex tw-flex-row tw-justify-center tw-gap-4">
          <Button
            size="sm"
            onClick={navigation.onClickBack}
            className="tw-flex-1 tw-rounded-xl"
            variant="secondary"
          >
            Back
          </Button>
          <Button
            size="sm"
            className="tw-flex-1 tw-rounded-xl"
            variant="primary"
            onClick={handleSubmission}
          >
            Next
            <ArrowRight className="tw-ml-1 tw-h-4 tw-w-4" />
          </Button>
        </div>
      </ModalFooter>
    </FormProvider>
  );
};
